import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useCases } from '@/hooks';
import {
  CATEGORY_LABELS,
  MODERATION_CATEGORIES,
  type ModerationCategory,
  type CaseStatus,
} from '@/core/models/case';
import { filterQueue } from '../filterCases';
import { STATUS_LABEL } from '../meta';
import { CaseList } from '../components/CaseList';

const Filters = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
  align-items: end;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};

  select,
  input {
    padding: 8px 10px;
    border-radius: ${({ theme }) => theme.radius.md};
    background: ${({ theme }) => theme.color.glassBg};
    border: 1px solid ${({ theme }) => theme.color.glassBorder};
    color: ${({ theme }) => theme.color.textPrimary};
    font-size: ${({ theme }) => theme.fontSize.sm};
  }
`;

const ALL_STATUSES = Object.keys(STATUS_LABEL) as CaseStatus[];

export function CaseQueueSection() {
  const { cases } = useCases();
  const [category, setCategory] = useState<ModerationCategory | 'all'>('all');
  const [status, setStatus] = useState<CaseStatus | 'all'>('all');
  const [search, setSearch] = useState('');
  const visible = useMemo(
    () => filterQueue(cases, { category, status, search }),
    [cases, category, status, search],
  );

  return (
    <div>
      <Filters>
        <Field>
          Category
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as ModerationCategory | 'all')}
          >
            <option value="all">All categories</option>
            {MODERATION_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
        </Field>
        <Field>
          Status
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as CaseStatus | 'all')}
          >
            <option value="all">All statuses</option>
            {ALL_STATUSES.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </Field>
        <Field>
          Search
          <input
            type="search"
            placeholder="reporter, team, post/account id"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Field>
      </Filters>
      {/* Same shape mobile already uses (CouncilScreen.tsx): only blame the filters
          when some are set. On prod the queue is empty for the ordinary reason —
          no case has ever been opened — and a member opening /council on the
          default view was being told a filter had hidden something. */}
      <CaseList
        cases={visible}
        emptyLabel={
          category !== 'all' || status !== 'all' || search.trim()
            ? 'No cases match these filters.'
            : 'No cases in the queue.'
        }
      />
    </div>
  );
}
